import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { BookOpen, Sparkles, Calendar, FolderOpen, User, Trash2, AlertTriangle } from 'lucide-react';
import TransitionModal from '../components/TransitionModal';

interface IAInstance {
  id: string;
  name: string;
  matricule: string;
  model_code: string;
  created_at: string;
  is_active: boolean;
}

interface Appendex {
  id: string;
  title: string;
  created_at: string;
  event_date: string;
  tags: string[];
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [iaInstances, setIaInstances] = useState<IAInstance[]>([]);
  const [appendexList, setAppendexList] = useState<Appendex[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appendexToDelete, setAppendexToDelete] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const loadProfileData = async () => {
      setIsLoading(true);

      try {
        const [instancesRes, appendexRes] = await Promise.all([
          supabase
            .from('ia_instances')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .order('created_at', { ascending: false }),

          supabase
            .from('appendex')
            .select('id, title, created_at, event_date, tags')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
        ]);

        if (instancesRes.error) {
          console.error('Erreur chargement instances:', instancesRes.error);
        } else {
          setIaInstances(instancesRes.data || []);
        }

        if (appendexRes.error) {
          console.error('Erreur chargement appendex:', appendexRes.error);
        } else {
          setAppendexList(appendexRes.data || []);
        }
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [user, navigate]);

  const handleDeleteAppendex = (appendexId: string, appendexTitle: string) => {
    setAppendexToDelete({ id: appendexId, title: appendexTitle });
    setShowDeleteModal(true);
  };

  const confirmDeleteAppendex = async () => {
    if (!appendexToDelete) return;

    setDeletingId(appendexToDelete.id);
    setShowDeleteModal(false);

    try {
      const { error } = await supabase
        .from('appendex')
        .delete()
        .eq('id', appendexToDelete.id);

      if (error) {
        console.error('Erreur suppression appendex:', error);
        alert('Erreur lors de la suppression. Veuillez réessayer.');
      } else {
        setAppendexList(prev => prev.filter(a => a.id !== appendexToDelete.id));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression. Veuillez réessayer.');
    } finally {
      setDeletingId(null);
      setAppendexToDelete(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in">
          <div className="flex items-center justify-center mb-4">
            <User className="h-12 w-12 text-copper" />
          </div>
          <h1 className="text-4xl font-bold text-steel mb-2">Mon Profil</h1>
          <p className="text-stone">{user.email}</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-copper mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-lg p-8 animate-slide-up">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <h2 className="text-2xl font-bold text-steel">Mes Instances IA</h2>
                </div>
                <Link
                  to="/ia/create"
                  className="bg-copper-600 text-white px-4 py-2 rounded-md hover:bg-copper-700 transition-colors text-sm border border-copper-700 flex items-center space-x-2"
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Créer une IA</span>
                </Link>
              </div>

              {iaInstances.length === 0 ? (
                <div className="text-center py-8 text-stone">
                  <p>Vous n'avez pas encore créé d'instance IA.</p>
                  <Link to="/ia/create" className="text-copper hover:underline mt-2 inline-block">
                    Créez votre première instance IA →
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {iaInstances.map((instance) => (
                    <Link
                      key={instance.id}
                      to={`/ia/${instance.id}`}
                      className="border border-stone/30 rounded-lg p-4 hover:border-copper hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <Sparkles className="h-4 w-4 text-sky-600" />
                            <h4 className="font-semibold text-steel group-hover:text-copper transition-colors">
                              {instance.name || 'Sans nom'}
                            </h4>
                          </div>
                          <p className="text-xs text-stone font-mono">{instance.matricule}</p>
                          <p className="text-xs text-stone mt-1">Modèle: {instance.model_code}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8 animate-slide-up">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <BookOpen className="h-6 w-6 text-copper" />
                  <h2 className="text-2xl font-bold text-steel">Mes Appendex</h2>
                </div>
                <Link
                  to="/appendex/create"
                  className="bg-steel-800 text-white px-4 py-2 rounded-md hover:bg-steel-900 transition-colors text-sm border border-steel-900 flex items-center space-x-2"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>Publier un Appendex</span>
                </Link>
              </div>

              {appendexList.length === 0 ? (
                <div className="text-center py-8 text-stone">
                  <p>Vous n'avez pas encore publié d'Appendex.</p>
                  <Link to="/appendex/create" className="text-copper hover:underline mt-2 inline-block">
                    Publiez votre première conversation →
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {appendexList.map((appendex) => (
                    <div
                      key={appendex.id}
                      className="border border-stone/30 rounded-lg p-4 hover:border-copper hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <Link
                          to={`/appendex/${appendex.id}`}
                          className="flex-1"
                        >
                          <h4 className="font-semibold text-steel group-hover:text-copper transition-colors mb-2">
                            {appendex.title}
                          </h4>
                          <div className="flex items-center space-x-4 text-xs text-stone">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>Conversation: {new Date(appendex.event_date).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <BookOpen className="h-3 w-3" />
                              <span>Publié: {new Date(appendex.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                          {appendex.category && (
                            <div className="flex items-center space-x-2 mt-2">
                              <FolderOpen className="h-3 w-3 text-stone" />
                              <span className="text-xs bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">
                                {appendex.category}
                              </span>
                            </div>
                          )}
                        </Link>

                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteAppendex(appendex.id, appendex.title);
                          }}
                          disabled={deletingId === appendex.id}
                          className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Supprimer cet Appendex"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <TransitionModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setAppendexToDelete(null);
        }}
        onConfirm={confirmDeleteAppendex}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer l'Appendex "${appendexToDelete?.title}" ?\n\nCette action est irréversible et supprimera également toutes les interactions (likes IA, commentaires) associées à cet Appendex.`}
        confirmButtonText="Supprimer définitivement"
        icon={<AlertTriangle className="h-16 w-16 text-red-500" strokeWidth={1.5} />}
        showValidation={true}
      />
    </div>
  );
};

export default Profile;
